/**
 * Web-billing handoff helpers for the mobile app.
 *
 * The mobile app's primary billing surface lives in `services/billing/*` and
 * the `useBilling()` hook — those talk to the dedicated `/billing/*` API
 * service for plans, purchases, restores, coupons, payment methods, and
 * transactions on iOS (Apple IAP) and Android/desktop-fallback (Stripe).
 *
 * This file used to also house thin wrappers around the legacy `/payments/*`
 * client routes (apply-coupon, cancel-subscription, verify-discount,
 * fetchPlans). Those are gone — `services/billing` exposes the same
 * functionality against the new service. What remains here are the auto-login
 * + external browser helpers used to bounce the user from a "Manage Plan" CTA
 * into the web app for actions we deliberately do NOT execute in-app on iOS:
 *
 *   • Per App Store Review Guideline 3.1.1(a), US storefront apps may include
 *     buttons / external links that open the default browser for purchases
 *     made outside IAP. We use `Linking.openURL` (Safari/Chrome on the system
 *     browser) — not WKWebView / SFSafariViewController sheets — because
 *     in-app web views count as "purchasing in the app" and trip 3.1.1.
 *
 *   • The auto-login ticket (`/auth/device-login/create/...`) is an auth-
 *     service operation, not a billing-service one, so it lives on the same
 *     base API endpoint as the rest of `apiClient`.
 *
 * When the React Native build replaces the web app entirely we can delete
 * these helpers and any `openBillingPage(...)` callsites along with them.
 */
import { Alert, Linking } from 'react-native';
import { getStoreState } from './_sessionIds';
import { apiPost, getFreshToken } from '../../utilities/apiClient';
import { getStoredTokens } from '../../utilities/tokenStore';
import config from '../../config';
import debug from '../../utilities/debug';

function getIds() {
  const state = getStoreState();
  const account = state.session?.account;
  const user = state.authentication?.user;
  return {
    accountId: account?.account_id as string,
    cognitoId: user?.cognito_id as string,
  };
}

/**
 * Creates a short-lived device-login ticket that encodes the given
 * redirectPath, then builds the auto-login URL for the web app.
 *
 * Mobile calls this before opening any payment-adjacent action that we want
 * the user to complete on web (e.g. legacy paid-flows still on Stripe). The
 * URL opens in the system browser, the web app redeems the ticket, refreshes
 * the Cognito session on the web side, and navigates to redirectPath.
 */
export async function generateBillingLink(redirectPath = '/billing'): Promise<string | null> {
  const { accountId, cognitoId } = getIds();
  if (!accountId || !cognitoId) {
    debug.warn('generateBillingLink: missing accountId or cognitoId', {
      hasAccountId: !!accountId,
      hasCognitoId: !!cognitoId,
    });
    return null;
  }

  try {
    // Ensure we attach freshly valid id/access tokens to the ticket payload.
    // apiPost() refreshes auth for the request header, but we also need fresh
    // tokens in the body so the web auto-login handoff doesn't start with
    // stale JWTs.
    const freshIdToken = await getFreshToken();
    if (!freshIdToken) {
      debug.warn('generateBillingLink: unable to refresh auth token before ticket create');
      return null;
    }

    const tokens = await getStoredTokens();
    if (!tokens?.refreshToken || !tokens?.idToken || !tokens?.accessToken) {
      debug.warn('generateBillingLink: no refresh token in storage', {
        hasTokens: !!tokens,
        hasIdToken: !!tokens?.idToken,
        hasAccessToken: !!tokens?.accessToken,
        refreshTokenLength: tokens?.refreshToken?.length ?? 0,
      });
      return null;
    }

    debug.log('authentication', 'generateBillingLink: creating ticket', {
      redirectPath,
      accountId: accountId.slice(0, 8),
    });

    const res = await apiPost(
      `/auth/device-login/create/${encodeURIComponent(accountId)}/${encodeURIComponent(cognitoId)}`,
      {
        refresh_token: tokens.refreshToken,
        id_token: tokens.idToken,
        access_token: tokens.accessToken,
        device_key: tokens.deviceKey || null,
        redirect_path: redirectPath,
      }
    );

    if (!res?.success || !res?.ticket_id) {
      debug.warn('generateBillingLink: ticket creation failed', {
        success: res?.success,
        message: res?.message,
        status: res?.status,
        hasTicketId: !!res?.ticket_id,
      });
      return null;
    }

    const base = config.webApp.baseUrl;
    const url =
      `${base}/auto-login` +
      `?ticket=${encodeURIComponent(res.ticket_id)}` +
      `&account_id=${encodeURIComponent(accountId)}` +
      `&cognito_id=${encodeURIComponent(cognitoId)}` +
      `&redirect=${encodeURIComponent(redirectPath)}`;

    debug.log('authentication', 'generateBillingLink: success', {
      redirectPath,
      url: url.slice(0, 80) + '…',
    });
    return url;
  } catch (err: any) {
    debug.error('generateBillingLink error', { message: err?.message, name: err?.name });
    return null;
  }
}

/**
 * Create a device-login URL the user can scan from another device to
 * inherit the current session. Wrapper around `generateBillingLink` —
 * same underlying ticket-create flow, but defaults to a neutral
 * post-redeem path (`/projects`) so the scan lands the user on the
 * project list rather than the billing screen.
 *
 * Used by SettingsScreen → "Sign in on another device" → QR sheet.
 */
export async function generateDeviceLoginUrl(redirectPath = '/projects'): Promise<string | null> {
  return generateBillingLink(redirectPath);
}

/**
 * Opens the web billing page in the user's default browser via Linking.openURL.
 *
 * This deliberately leaves the app (rather than opening an in-app Safari
 * sheet) so any purchase the user makes on the web happens entirely outside
 * the app environment — the condition under which App Store Guideline 3.1.1(a)
 * permits non-IAP purchase links on the US storefront.
 */
export async function openBillingPage(redirectPath = '/billing'): Promise<void> {
  const url = await generateBillingLink(redirectPath);
  if (!url) {
    await new Promise<void>((resolve) => {
      Alert.alert(
        'Open in Browser',
        "We couldn't generate a secure login link. You'll need to sign in on the web app.",
        [
          {
            text: 'Open Anyway',
            onPress: async () => {
              try {
                await Linking.openURL(`${config.webApp.baseUrl}/billing`);
              } catch (err) {
                debug.error('openBillingPage fallback Linking.openURL failed', err);
              }
              resolve();
            },
          },
          { text: 'Cancel', style: 'cancel', onPress: () => resolve() },
        ],
        { cancelable: true, onDismiss: () => resolve() },
      );
    });
    return;
  }
  try {
    await Linking.openURL(url);
  } catch (err) {
    debug.error('openBillingPage Linking.openURL failed', err);
  }
}
